import type { PluginStateBucket, PluginStatus } from "@/services/pluginStatusApi";

// Worker states of the /plugins page — presentation only. The state itself is
// computed by the backoffice (`_classify_worker_state`,
// backoffice/src/features/plugin_status/router.py) for the « Par état » chart
// (`aggregates.by_state`) AND for each row (`worker_state`): one classifier,
// one clock — the server's. This module maps the stable English keys to French
// labels and semantic colours.

// « Par état » chart: French label + SEMANTIC colour per key. No rotating
// palette: green = scraping, amber/blue = throttled or paused, grey = inactive
// or anonymous (user behaviour, nothing to fix), red/magenta = to investigate.
// An unknown key (backoffice newer than the admin) falls back to the grey
// "Autres" colour with its raw key.
export const STATE_META: Record<string, { label: string; color: string }> = {
  active: { label: "Actif", color: "hsl(152 76% 36%)" },
  // A job running past the poll windows (no poll during a job): neutral.
  busy: { label: "Occupé (job en cours)", color: "hsl(199 60% 45%)" },
  late: { label: "Retard (30 s–2 min)", color: "hsl(45 93% 47%)" },
  stalled: { label: "Décroché (> 2 min)", color: "hsl(25 95% 53%)" },
  quota_blocked: { label: "Quota saturé", color: "hsl(32 95% 44%)" },
  silent_no_tab: { label: "Inactif — onglet LinkedIn indispo", color: "hsl(215 14% 45%)" },
  silent: { label: "Inactif", color: "hsl(215 16% 30%)" },
  offline: { label: "Hors ligne", color: "hsl(214 20% 78%)" },
  anonymous: { label: "Anonyme (sans JWT)", color: "hsl(200 10% 58%)" },
  checkpoint: { label: "Vérification LinkedIn", color: "hsl(350 89% 60%)" },
  jwt_invalid: { label: "JWT rejeté", color: "hsl(292 84% 61%)" },
  paused_admin: { label: "Pause admin", color: "hsl(217 91% 60%)" },
  no_linkedin_session: { label: "Sans session LinkedIn", color: "hsl(262 83% 65%)" },
  lease_blocked: { label: "Bail — autre navigateur", color: "hsl(192 91% 36%)" },
  ghost: { label: "Heartbeat perdu (polle encore)", color: "hsl(0 72% 51%)" },
};

// Health grouping of the states in 4 families. Goal: break the false « all red »
// impression — most non-green states are EXPECTED pauses (anti-ban quota, admin
// pause, logged out of LinkedIn) or inactivity (browser open without a LinkedIn
// tab, plugin closed), not failures. Only `broken` deserves an investigation.
// An unknown key lands in `broken`, by prudence.
// NB: the `PluginSilentWorkersHigh` alert (infra repo) still counts
// silent/silent_no_tab — to be realigned on this grouping.
export type HealthGroup = "healthy" | "expected" | "inactive" | "broken";

export const STATE_HEALTH: Record<string, HealthGroup> = {
  active: "healthy",
  busy: "healthy",
  late: "healthy",
  quota_blocked: "expected",
  paused_admin: "expected",
  no_linkedin_session: "expected",
  lease_blocked: "expected",
  checkpoint: "expected",
  anonymous: "expected",
  silent: "inactive",
  silent_no_tab: "inactive",
  offline: "inactive",
  stalled: "broken",
  jwt_invalid: "broken",
  ghost: "broken",
};

export const HEALTH_META: Record<HealthGroup, { label: string; color: string; dot: string }> = {
  healthy: { label: "Sain", color: "text-emerald-700", dot: "bg-emerald-500" },
  expected: { label: "Normal / attendu", color: "text-blue-700", dot: "bg-blue-500" },
  inactive: { label: "Inactif", color: "text-slate-600", dot: "bg-slate-400" },
  broken: { label: "À investiguer", color: "text-red-700", dot: "bg-red-500" },
};

export const HEALTH_ORDER: HealthGroup[] = ["healthy", "expected", "inactive", "broken"];

export function healthGroupOf(stateKey: string): HealthGroup {
  return STATE_HEALTH[stateKey] ?? "broken";
}

export function healthTotals(buckets: PluginStateBucket[]): Record<HealthGroup, number> {
  const totals: Record<HealthGroup, number> = { healthy: 0, expected: 0, inactive: 0, broken: 0 };
  for (const b of buckets) {
    totals[healthGroupOf(b.key)] += b.count;
  }
  return totals;
}

/**
 * Seconds elapsed since `iso`. The backoffice value (`*_age_s`, server clock)
 * wins; the local computation only serves an older backoffice that does not
 * send it yet (rolling deploy).
 */
export function ageSeconds(
  serverAge: number | null | undefined,
  iso: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (typeof serverAge === "number") return serverAge;
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((nowMs - at) / 1000));
}

export function formatAge(seconds: number | null): string {
  if (seconds === null) return "—";
  return `${formatDuration(seconds)} ago`;
}

/** A duration in the unit of `formatAge`, without the « ago ». */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

// Same window as the backoffice (`_WORKER_ERROR_ALERT_MAX_AGE_S`) — only used
// for an older backoffice that does not send its verdict yet.
const WORKER_ERROR_ALERT_MAX_AGE_S = 30 * 60;

/**
 * Whether the row shows the ⚠ of `last_worker_error` (plan 5.1): an error
 * younger than 30 min and not transient. The backoffice verdict
 * (`last_worker_error_active`, server clock) wins; the local computation only
 * serves an older backoffice (rolling deploy).
 */
export function isWorkerErrorAlert(plugin: PluginStatus, nowMs: number = Date.now()): boolean {
  const error = plugin.last_worker_error;
  if (!error) return false;
  if (typeof plugin.last_worker_error_active === "boolean") return plugin.last_worker_error_active;
  if ((error.severity ?? "error").toLowerCase() === "transient") return false;
  const at = new Date(error.ts);
  const iso = Number.isNaN(at.getTime()) ? null : at.toISOString();
  const age = ageSeconds(plugin.last_worker_error_age_s, iso, nowMs);
  return age !== null && age < WORKER_ERROR_ALERT_MAX_AGE_S;
}

// Heartbeat: Redis key with a 90 s TTL (back/src/features/plugin_status/
// services.py), refreshed every 15-60 s by the plugin's two emitters. Green =
// fresh (< 30 s); amber = only the slow emitter gets through (service-worker
// alarm late); red = past the TTL, the key should have expired — a bug symptom.
export function heartbeatAgeColor(seconds: number | null): string {
  if (seconds === null) return "text-muted-foreground";
  if (seconds < 30) return "text-emerald-600";
  if (seconds < 90) return "text-amber-600";
  return "text-red-600";
}

/** Why the last /next-job Bearer was refused (back plan 5.5). */
export const AUTH_REJECTION_LABEL: Record<string, string> = {
  expired: "expiré",
  invalid: "invalide",
  other: "vérification impossible",
};

export type WorkerActivityState = {
  color: string;
  label: string;
  severity: "ok" | "warn" | "bad" | "muted";
};

// Backoffice that predates `worker_state`: coarse reading of the raw fields,
// same rules as before (for the length of a rolling deploy).
function legacyState(plugin: PluginStatus): string {
  if (!plugin.last_next_job_call) return "silent";
  if (plugin.next_job_authenticated === false && plugin.user_id) return "jwt_invalid";
  if (plugin.last_job_type === "paused") return "paused_admin";
  if (plugin.has_linkedin_session === false) return "no_linkedin_session";
  if (plugin.last_job_type === "lease_blocked") return "lease_blocked";
  if (plugin.last_job_type === "quota_blocked") return "quota_blocked";
  const lag = ageSeconds(plugin.activity_age_s, plugin.last_next_job_call) ?? Infinity;
  if (lag < 30) return "active";
  if (lag < 120) return "late";
  return "stalled";
}

/**
 * « Worker actif » badge of a row: the state the backoffice computed for THIS
 * snapshot (the same one as the chart), with the age of the last /next-job for
 * the polling states. Distinct from the heartbeat: a plugin can be online
 * (service worker alive) without polling (no LinkedIn tab, silent content
 * script).
 */
export function workerActivityState(plugin: PluginStatus): WorkerActivityState {
  // null = the backoffice could not read this worker's activity: say nothing
  // rather than « inactif » on missing data.
  if (plugin.worker_state === null) {
    return { color: "text-muted-foreground", label: "—", severity: "muted" };
  }
  const lagLabel = formatAge(ageSeconds(plugin.activity_age_s, plugin.last_next_job_call));
  const state = plugin.worker_state ?? legacyState(plugin);
  switch (state) {
    case "active":
      return { color: "text-emerald-600", label: lagLabel, severity: "ok" };
    case "busy": {
      // No poll while a job runs: neither late nor stalled, just working.
      const since = formatDuration(
        plugin.job_running_age_s ?? ageSeconds(plugin.activity_age_s, plugin.last_next_job_call),
      );
      return { color: "text-sky-700", label: `job en cours depuis ${since}`, severity: "ok" };
    }
    case "late":
      return { color: "text-amber-600", label: lagLabel, severity: "warn" };
    case "stalled":
      return { color: "text-red-600", label: lagLabel, severity: "bad" };
    case "quota_blocked":
      return {
        color: "text-amber-600",
        label: `quota (${plugin.blocked_bucket ?? "?"})`,
        severity: "warn",
      };
    case "paused_admin":
      return {
        color: "text-blue-600",
        label: `pause admin${plugin.paused_mode ? ` (${plugin.paused_mode})` : ""}`,
        severity: "warn",
      };
    case "no_linkedin_session":
      return { color: "text-violet-600", label: "sans session LinkedIn", severity: "warn" };
    case "lease_blocked":
      return { color: "text-cyan-700", label: "bail — autre navigateur", severity: "warn" };
    case "checkpoint":
      // The « Vérification LinkedIn requise » banner of the cell already says why.
      return { color: "text-amber-700", label: "en pause", severity: "warn" };
    case "jwt_invalid": {
      const why = AUTH_REJECTION_LABEL[plugin.next_job_auth_rejected ?? ""];
      return {
        color: "text-red-600",
        label: why ? `JWT rejeté (${why})` : "JWT rejeté",
        severity: "bad",
      };
    }
    case "anonymous":
      return { color: "text-slate-500", label: "anonyme (sans JWT)", severity: "muted" };
    case "silent_no_tab":
      return { color: "text-slate-500", label: "inactif — pas d'onglet LinkedIn", severity: "muted" };
    case "silent":
      return { color: "text-slate-500", label: "inactif", severity: "muted" };
    case "offline":
      return { color: "text-slate-500", label: "hors ligne", severity: "muted" };
    case "ghost":
      return { color: "text-red-600", label: "heartbeat perdu", severity: "bad" };
    default:
      return { color: "text-muted-foreground", label: state, severity: "muted" };
  }
}

/** « Authentifié » line (tooltip + drawer): a refused JWT ≠ a poll without JWT. */
export function nextJobAuthLabel(plugin: PluginStatus): string {
  if (plugin.next_job_authenticated === true) return "✓";
  if (plugin.next_job_auth_rejected) {
    const why = AUTH_REJECTION_LABEL[plugin.next_job_auth_rejected] ?? plugin.next_job_auth_rejected;
    return `✗ JWT rejeté (${why})`;
  }
  if (plugin.next_job_auth_header === false) return "✗ sans JWT (anonyme)";
  // Trace written by a back pod that predates header tracing: cause unknown.
  if (plugin.next_job_authenticated === false) return "✗ non authentifié";
  return "—";
}

/** A JWT really refused at the last /next-job — the backoffice's verdict. */
export function isJwtRejected(plugin: PluginStatus): boolean {
  // null (activity unreadable) counts as not rejected.
  if (plugin.worker_state !== undefined) return plugin.worker_state === "jwt_invalid";
  // Backoffice that predates `worker_state`: historical reading.
  return plugin.next_job_authenticated === false && !!plugin.user_id;
}
